<script setup lang="ts">
import type { CarEvent } from '../internal/types.ts'
import { compareLocalDate, formatDate } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ events: readonly CarEvent[] }>()
defineEmits<{ edit: [eventId: string]; delete: [eventId: string] }>()

const { t, locale } = useI18n()

const sorted = computed(() => [...props.events].sort((a, b) => compareLocalDate(b.date, a.date)))
</script>

<template>
  <p v-if="events.length === 0" class="text-[color:var(--color-text-muted)]">
    {{ t('garage.events.empty') }}
  </p>

  <ul v-else class="flex flex-col gap-2">
    <li
      v-for="event in sorted"
      :key="event.id"
      class="flex items-center justify-between gap-3 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-3 bg-[color:var(--color-surface)]"
    >
      <div class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text)]">{{
          t(`garage.eventType.${event.type}`)
        }}</span>
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ formatDate(event.date, locale as 'en' | 'hu') }}
          <template v-if="event.mileageKm !== undefined"> · {{ event.mileageKm }} km</template>
        </span>
        <span
          v-if="event.notes"
          class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >{{ event.notes }}</span
        >
        <span
          v-if="event.journalMode !== undefined"
          class="text-[length:var(--text-step-1)] text-[color:var(--color-accent)]"
        >
          {{ t('garage.events.linkedEntry') }}
        </span>
      </div>
      <div class="flex gap-2">
        <button
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-2 py-1 text-[length:var(--text-step-1)] text-[color:var(--color-text)]"
          :aria-label="`${t('garage.events.edit')} ${t(`garage.eventType.${event.type}`)}`"
          @click="$emit('edit', event.id)"
        >
          {{ t('garage.events.edit') }}
        </button>
        <button
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-2 py-1 text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
          :aria-label="`${t('garage.events.delete')} ${t(`garage.eventType.${event.type}`)}`"
          @click="$emit('delete', event.id)"
        >
          {{ t('garage.events.delete') }}
        </button>
      </div>
    </li>
  </ul>
</template>
